import { difference, flatten } from 'lodash';
import { toastr } from 'react-redux-toastr';
import { Get, Post, Put, HOST } from './../helpers/ajax';
import { utils } from './../helpers';
import { signalR as signalRHelpers } from './../helpers';
import { auth, storage, httpRequestHeader } from './../helpers';
import { trackPromise } from 'react-promise-tracker';

export const byCustomResponseHandler = (response, prop) =>
  utils.propertyReducer(response, d => d[prop], d => {
    if (d[prop] === undefined) {
      console.error(`
        byCustomResponseHandler from api.js is trying to map an array by ${prop}
        but object below has no ${prop}:
      `);
      console.error(d);
    }

    return d;
  });

export const byIdResponseHandler = response => byCustomResponseHandler(response, 'id');

/**
 * Returns a function that handles typed (request, success, failure) responses with its
 * arguments and dispatches the appropriate actions.
 * @param {{ request: string, success: string, failure: string }} actionTypes
 * - The action names to dispatch for different events.
 * @param {string} requestName - The name of the request. Used for the request caching
 *   reducer to determine which the structure of the given request and its arguments.
 *   Default value is 'request'.
 * @param {(response) => any} responseHandler - Used for mutating the structure of the
 *   response. Default value is 'baseResponseHandler' which simply creates a map by 'id'
 *   prop from within the object.
 * @param {string} errorMessage - If provided, will be a custom error message to show.
 *   Default value is 'Unknown error occerred'.
 * @param {boolean} error - If provided, the response variable provided will be displayed
 * in a toast message instead of the errorMessage param. This allows backend errors to be
 * displayed.
 */
export const apiActionDispatcher = ({
  actionTypes,
  requestName = 'request',
  responseHandler = byIdResponseHandler,
  successMessage = undefined,
  errorMessage = 'Unknown error occurred',
  useResponseErrorMessage = false,
  errorExtendedTimeout = 0
}) =>
  (type, args, response, dispatch, singleThing) => {
    const result = {
      type: actionTypes[type],
      requestName,
      args,
      isDone: singleThing
    };

    switch (type) {
      case 'success':
        if (successMessage) {
          toastr.success('Success!', successMessage);
        }
        result['response'] = responseHandler ?
          responseHandler(response, dispatch) :
          response;
        break;
      case 'failure':
        const message = useResponseErrorMessage ? response : errorMessage;
        const errorOptions = { timeOut: errorExtendedTimeout || undefined };
        toastr.error('Error', message, errorOptions);
        result['message'] = message;
        break;
      case 'delete':
        result['response'] = response;
        break;
    }

    return result;
  };

/**
 * Initiates multiple webSocketWrapperApiCalls in parallel by splitting the ids by the
 * provided chunksize.
 * @param {any[]} ids - The ids to request.
 * @param {number} chunkSize - The number of ids to send per request.
 * @param {(newIds: any[]) => (type, args, response => any)} dispatcher - The dispatcher
 *   to use to dispatch actions. See the apiActionDispatcher for an example. Should be
 *   the same as apiCall.
 * @param {(optionalArgs) => Promise<any>} apiFunc - The request to execute
 * @param {(state) => { inProgress: boolean, args: any, isDone: any[] }} checkRequestCache
 * - Function used to determine if all arguments currently being requested have already
 *   been requested (pending request count). This should return a subset of arguments
 *   if any have not been requested. Should be used with the cachedRequestReducer.
 */
export const chunkedMultiIdApiCall = (
  ids,
  chunkSize,
  dispatcher,
  apiFunc,
  checkRequestCache,
  requestHeader
) => async (dispatch, getState) => {
  if (!ids) {
    return false;
  }

  const promises = [];
  for (let i = 0; i < ids.length; i += chunkSize) {
    const newIds = ids.slice(i, i + chunkSize);
    promises.push(
      multiIdApiCall(
        dispatcher,
        apiFunc,
        checkRequestCache,
        newIds,
        null,
        null,
        requestHeader
      )(dispatch, getState)
    );
  }

  const results = await Promise.all(promises);
  return results.find(r => !r) === undefined;
};

/**
 * Wraps an apiCall with a websocket such that a successful request will initiate a
 * subscription with those same args if there are any.
 * @param {func} apiCall - See apiCall function.
 * @param {(type, args, response => any)} dispatcher - The dispatcher to use to dispatch
 *   actions. See the apiActionDispatcher for an example. Should be the same as apiCall.
 * @param {WebSocketManager} webSocketManager - The web socket manager.
 * @param {string} subscriptionType - The type of subscription.
 * @param {(state) => any} getArgs - Function used to get all of the args requested since
 *   the dawn of time out of the state.
 */
export const webSocketWrappedApiCall = (
  apiCall,
  dispatcher,
  webSocketManager,
  subscriptionType,
  getArgs
) => async (dispatch, getState) => {
  // set the dispatcher for connection status updates if not set.
  webSocketManager._hasDispatcher() ?
    null :
    webSocketManager.setDispatcher(dispatch, getState);

  let result = null;
  try {
    result = await apiCall(dispatch, getState);
  }
  catch (e) {
    // apiCall is expected only to resolve as bool
    if (process.env.NODE_ENV !== 'test') {
      console.error('apiCall produced unexpected promise rejection.');
    }
  }

  if (!result) {
    return result;
  }

  const args = getArgs ? getArgs(getState()) : undefined;
  try {
    await webSocketManager.subscribe(
      subscriptionType,
      args ? { ids: args } : undefined,
      (response) => {
        const action = dispatcher('success', args, response);
        if (!action.type) {
          console.error(
            'Error while dispatching websocket change. ' +
            'The action being dispatch is not formatted properly'
          );
        }
        dispatch(action);
      }
    );
  }
  catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error(error);
    }
  }

  return result;
};


/**
 * Wraps an apiCall with a signalr hub such that a successful request will initiate a
 * subscription with those same args if there are any. If get an unsuccessful request
 * but the subscriptionType is not subscribed, signalRHubManager will be called
 * to subscribe the subscriptionType with args.
 * @param {func} apiCall - See apiCall function.
 * @param {(type, args, response => any)} dispatcher - The dispatcher to use to dispatch
 *   actions. See the apiActionDispatcher for an example. Should be the same as apiCall.
 * @param {signalRHubManager} signalRHubManager - The signalRHub manager.
 * @param {string} subscriptionType - The type of subscription.
 * @param {(state) => any} getArgs - Function used to get all of the args requested since
 *   the dawn of time out of the state.
 */
export const signalRWrappedApiCall = (
  apiCall,
  dispatcher,
  signalRHubManager,
  subscriptionType,
  getArgs,
  payloadsHandler
) => async (dispatch, getState) => {
  // set the dispatcher for connection status updates if not set.
  signalRHubManager.hasDispatcher() ?
    null :
    signalRHubManager.setDispatcher(dispatch);

  let result = null;
  try {
    result = await apiCall(dispatch, getState);
  }
  catch (e) {
    // apiCall is expected only to resolve as bool
    if (process.env.NODE_ENV !== 'test') {
      console.error('apiCall produced unexpected promise rejection.');
    }
  }

  if (!result) {
    const subscribedTypes = signalRHubManager.getSubscribedTypes();
    if (subscribedTypes.has(subscriptionType))
    {
      return result;
    }
  }

  const args = getArgs ? getArgs(getState()) : undefined;
  try {
    await signalRHubManager.subscribe(
      subscriptionType,
      args ? { ids: args } : undefined,
      (response, messageType) => {
        let action;
        switch (messageType)
        {
          case signalRHelpers.RECEIVE_PAYLOAD:
            action = dispatcher('success', args, response);
            break;
          case signalRHelpers.DELETE_PAYLOAD:
            action = dispatcher('delete', args, response);
            break;
          default:
            console.error('Error while dispatching signalR change. ' +
            'The message type provided is unsupported');
            break;
        }

        if (!action.type) {
          console.error(
            'Error while dispatching signalR change. ' +
            'The action being dispatch is not formatted properly'
          );
        }
        dispatch(action);
      },
      payloadsHandler
    );
  }
  catch (error) {
    if (process.env.NODE_ENV !== 'test') {
      console.error(error);
    }
  }

  return result;
};

/**
 * Execute an api call but first check if the data already exists or theres a pending
 * request, and if so, don't do it.
 * @param {(type, args, response => any)} dispatcher - The dispatcher to use to dispatch
 *   actions. See the apiActionDispatcher for an example.
 * @param {(optionalArgs) => Promise<any>} apiFunc - The request to execute
 * @param {(state) => boolean} hasBeenLoaded - Function used to check if the data has
 *   already been loaded before.
 * @param {(isDone) => any} isFinalFetch - Function used to determine if isDone contains
 *   all information required. It will take in an object that will either be undefined
 *   or contain a list of items that have already been fetched and it will return either
 *   that same list or add to it based on if there has been more items fetched.
 * @param {(state) => { inProgress: boolean, args: any, isDone: any[] }} checkRequestCache
 * - Functionused to determine if all arguments currently being requested have already
 *   been requested (pending request count). This should return a subset of arguments
 *   if any have not been requested. Should be used with the cachedRequestReducer.
 * @param {any} args - The arguments to send along with the request
 * @param {function} errorFunc - A function that, if defined, will be called
 *   upon an error occurring.
 */
export const apiCall = (
  dispatcher,
  apiFunc,
  hasBeenLoaded,
  isFinalFetch,
  checkRequestCache,
  args,
  additionalArgs,
  errorFunc,
  requestHeader,
  isDone
) => async (dispatch, getState) => {
  const state = getState();
  if (hasBeenLoaded && hasBeenLoaded(state)) {
    // Data is already in state -> do nothing
    return false;
  }
  const cachedRequest = checkRequestCache ?
    checkRequestCache(state) :
    {
      inProgress: false,
      args,
      isDone
    };
  if (cachedRequest.inProgress) {
    return false;
  }
  const newIsDone = isFinalFetch && isFinalFetch(isDone);
  if (cachedRequest.isDone &&
    JSON.stringify(newIsDone) === JSON.stringify(cachedRequest.isDone)) {
    return false;
  }

  dispatch(dispatcher('request',
    cachedRequest.allArgs || cachedRequest.args, undefined, dispatch));
  args = cachedRequest.args;

  additionalArgs = additionalArgs ? additionalArgs.concat(args) : args;
  if (requestHeader) {
    const emailAddress = auth.checkAuthenticated() ?
      localStorage.getItem(storage.EMAIL_KEY) : '';
    requestHeader[httpRequestHeader.EMAIL_ADDRESS] = emailAddress;
    requestHeader[httpRequestHeader.PATH_NAME] = state.router.location.pathname;
    additionalArgs = [requestHeader, ...additionalArgs];
  }

  try {
    const response = await trackPromise(
      apiFunc(...(additionalArgs || [])));
    dispatch(dispatcher('success', args, response, dispatch, newIsDone));
    return true;
  } catch (error) {
    errorFunc ? errorFunc(error) : null;
    dispatch(dispatcher('failure', args, error, dispatch, null));
    if (process.env.NODE_ENV !== 'test') {
      console.error(error);
    }
    return false;
  }
};

/**
 * Wraps the apiActionDispatcher by providing a different responseHandler that groups
 * items in the response array by the ids that were given as arguments. Ex: requesting
 * jobs by builds ids [1, 2] will create { 1: [job1, job2], 2: [job3] }
 * @param {{ request: string, success: string, failure: string }} actionTypes - The
 *   action names to dispatch for different events.
 * @param {string} accumulatorIdProp - The property name used to find the ids that were
 *   requested from within the response items. Ex: request jobs by build ids ->
 *   accumulatorIdProp will be 'build_id' since that is the prop within jobs that we care
 *   about.
 * ! Passing this prop will produce array values for each key within the object
 * * Pass custom responseHandler without this prop to use caching without accumulator
 * @param {string} requestName - The name of the request. Used for the request caching
 *   reducer to determine which the structure of the given request and its arguments.
 *   Default value is 'request'.
 * @param {(response) => any} responseHandler - Used for mutating the structure of the
 *   response. Default value is 'baseResponseHandler' which simply creates a map by 'id'
 *   prop from within the object.
 * @param {string} errorMessage - If provided, will be a custom error message to show.
 *   Default value is 'Unknown error occerred'.
 */
export const multiIdApiActionDispatcher = ({
  actionTypes,
  accumulatorIdProp,
  requestName,
  responseHandler,
  errorMessage,
  emptyResult = () => []
}) => (newIds) => apiActionDispatcher({
  actionTypes,
  requestName,
  responseHandler: response => {
    const byId = accumulatorIdProp ?
      utils.propertyAccumulatorReducer(response, accumulatorIdProp) :
      response;
    newIds.forEach(nid => {
      if (!byId[nid]) {
        byId[nid] = emptyResult();
      }
    });

    return responseHandler ? responseHandler(byId) : byId;
  },
  errorMessage
});

/**
 * Extends the apiCall function by providing logic for only requesting ids that have not
 * already been requested.
 * @param {(ids) => (type, args, response => any)} dispatcher
 * The dispatcher to use to dispatch actions and is curried in this wrapper.
 * If using an apiActionDispatcher over a multiIdApiActionDispatcher then may have
 * to wrap with an arrow function inline
 * @param {(optionalArgs) => Promise<any>} apiFunc - The request to executec
 * @param {(state) => { inProgress: boolean, args: any, isDone: any[] }} checkRequestCache
 * - Function used to determine if all arguments currently being requested have already
 *   been requested (pending request count). This should return a subset of arguments
 *   if any have not been requested. Should be used with the cachedRequestReducer.
 * @param {any[]} ids - The list of ids to request.
 * @param {function} errorFunc - Passed to the apiCall as errorFunc
 */
export const multiIdApiCall = (
  dispatcher,
  apiFunc,
  checkRequestCache,
  ids,
  extraArgs,
  errorFunc,
  requestHeader
) => (dispatch, getState) => {
  if (!ids || ids.length === 0) {
    return false;
  }
  const state = getState();
  const newIdsRequest = checkRequestCache(state, ids);
  return apiCall(
    dispatcher(newIdsRequest.args),
    apiFunc,
    () => newIdsRequest.args.length === 0,
    null,
    () => newIdsRequest,
    [newIdsRequest.args],
    extraArgs,
    errorFunc,
    requestHeader,
    null
  )(dispatch, getState);
};

export const apiString = {
  media: {
    getStreamVideo: (videoLink) => {
      return HOST + 'sapi/media/streamvideo?filePath=' + encodeURI(videoLink);
    }
  }
};

export const api = {
  acquireTelemetry: {
    searchExceptionsByTerm: (dateSelected, device, image, search) =>
      Post('sapi/acquireTelemetry',
        { date: dateSelected, deviceType: device, imageType: image, searchTerm: search })
  },
  acquisitionsPage: {
    fetchAllMetrics: () =>
      Get('sapi/scenarios/AcquisitionTests/IMAGECREATIONDURATION/info'),
    fetchAllSizes: () =>
      Get('sapi/scenarios/AcquisitionTests/IMAGESIZE/info')
  },
  annotations: {
    fetchScenariosAnnotations: () =>
      Get('sapi/annotations/scenarios/recent'),
    fetchBuildAnnotations: () =>
      Get('sapi/annotations/builds/recent'),
    fetchAppAnnotations: (days) =>
      Get(`sapi/annotations/apps/recent?days=${days}`),
    addScenarioComment: (scenarioId, data) =>
      Post(`sapi/annotations/scenario/${scenarioId}/add`, data),
    addBuildComment: (buildId, data) =>
      Post(`sapi/annotations/build/${buildId}/add`, data),
    addAppComment: (appId, data) =>
      Post(`sapi/annotations/app/${appId}/add`, data),
    removeScenarioComment: (annotationId) =>
      Post(`sapi/annotations/scenario/${annotationId}/remove`),
    removeBuildComment: (annotationId) =>
      Post(`sapi/annotations/build/${annotationId}/remove`),
    removeAppComment: (annotationId) =>
      Post(`sapi/annotations/app/${annotationId}/remove`)
  },
  artifactHealth: {
    fetchPlatformStatuses: () => Get('sapi/art-health/platform-statuses'),
    fetchApplicationStatus: appSupportId =>
      Get(`sapi/art-health/app-status/${appSupportId}`),
    verifyArtifactApplication: (applicationInfo) =>
      Post('sapi/art-health/verify', applicationInfo)
  },
  auth: {
    authenticate: (authString) => Post('sapi/authenticate', authString),
    logout: () => Post('sapi/authenticate/logout', '')
  },
  aws: {
    fetchCostReport: (periodicity, lower, upper) => {
      return Get(`sapi/aws/costAndUsage/${periodicity}/${lower}/${upper}`);
    }
  },
  build: {
    fetchRecent: (days) => Get(`sapi/build/recent?days=${days}`),
    fetchSince: (timestamp) => Get(`sapi/build/since?after=${timestamp}`),
    fetchSinceByType: (type, timestamp) =>
      Get(`sapi/build/since/type?${type.toLowerCase()}=true${
        timestamp ? `&after=${timestamp}` : ''
      }`),
    fetchRCSinceLatestRelease: (product) =>
      Get(`sapi/build/rc/sinceLatestRelease?product=${product}`)
  },
  caseAmending: {
    updateBaseline: (imageName, jobType, jobNumber) =>
      Post(`sapi/caseamending/${imageName}/${jobType}/${jobNumber}/update`)
  },
  cloudAcquisitions: {
    fetchRecent: () => Get('sapi/cloudAcquisitions/recent'),
    fetchLatestSuccesfulBuildInfo: () => Get('sapi/cloudAcquisitions/lastSuccess')
  },
  dispatcher: {
    fetchAll: () => Get('sapi/dispatchers'),
    fetchAllByType: (dispatcherType) =>
      Get(`sapi/dispatchers/byType?type=${dispatcherType}`),
    fetchDispatcherByJobId: (jobId) => Get(`sapi/dispatchers/byJobId?jobId=${jobId}`),
    fetchDispatcherByNameAndNumber: (jobNameNumber) =>
      Get(`sapi/dispatcher/byNameNumber?jobNameNumber=${jobNameNumber}`),
    fetchRecent: (days) => Get(`sapi/dispatchers/recent?days=${days}`),
    fetchRecentByType: (dispatcherType, days) =>
      Get(`sapi/dispatchers/recent?type=${dispatcherType}&days=${days}`),
    fetchDispatchersByBuildIds: (...buildIds) =>
      Post('sapi/dispatchers/byBuildIds', { ids: buildIds.join(',') })
  },
  installerFiles: {
    fetchDifferences: (buildId, baselineBuildId, product) => Get(
      'sapi/installer/differencesByBuildId?' +
      `buildId=${buildId}&` +
      `baselineBuildId=${baselineBuildId}&` +
      `product=${product}`
    ),
    fetchInstallerTestExpected: (...jobIds) =>
      Post('sapi/installer/filepresence', { ids: jobIds.join(',') }),
    setExpected: (expected, fileIds) => Post(
      `sapi/installer/files/setExpected?expected=${expected}`,
      { ids: fileIds.join(',') }
    ),
    installerSetWhiteTestExpected: (filename, presenceId, expected, username) =>
      Post('sapi/installer/setWhiteExpected',
        { filename, presenceId, expected, username })
  },
  jobs: {
    fetchJobByNameAndNumber: (jobName, jobNumber) =>
      Get(`sapi/jobs?jobName=${jobName}&jobNumber=${jobNumber}`),
    fetchByJobIds: (...jobIds) => Post('sapi/jobs/jobsByIds', { ids: jobIds.join(',') }),
    fetchByBuildIds: (...buildIds) => Post('sapi/jobs', { ids: buildIds.join(',') }),
    fetchRecentDispatcherJobsByDispatcherType: (dispatcherType) =>
      Get(`sapi/jobs/recent/${dispatcherType}`),
    fetchByDateRange: (names, lower, upper) => {
      const data = (...nameData) => nameData.join(',');
      return Post(`sapi/jobs/date/${lower}/${upper}`, { names: data(names) });
    }
  },
  jenkins: {
    toggleJenkinsBuildForever: (jobName, server, jobNumber) =>
      Get(`sapi/jenkins/forever/${jobName}/${jobNumber}/toggle?server=${server}`),
    triggerJobFromId: (id) => Post(`sapi/jenkins/${id}/trigger`),
    triggerWhatsIn: (oldVersion, newVersion, product) =>
      Post(`sapi/jenkins/whats-in/trigger/${oldVersion}/${newVersion}/${product}`),
    triggerBaselineUpdate: (platform, application, jobNumber) =>
      Post('sapi/jenkins/run-artifact-regression-baseline-update/trigger/' +
      `${platform}/${application}/${jobNumber}`)
  },
  jira: {
    fetchScans: (authToken, step, startAt) => Get(
      `sapi/jira/issues/SCAN?step=${step}&startAt=${startAt}`,
      { authToken: authToken }
    ),
    fetchScanCount: (authToken) => Get(
      'sapi/jira/issues/SCAN/count',
      { authToken }
    ),
    updateExpectedHits: async (
      issueKey,
      newExpectedHitCounts,
      newNcfDurationAverage,
      newCcfDurationAverage,
      newExpectedSourceCounts,
      newExpectedAttributeCounts,
      authToken
    ) => {
      Put(
        `sapi/jira/${issueKey}/update/hitCounts`,
        { ...newExpectedHitCounts },
        { authToken: authToken }
      );
      Put(
        `sapi/jira/${issueKey}/update/averageNcfDuration`,
        newNcfDurationAverage,
        { authToken: authToken }
      );
      Put(
        `sapi/jira/${issueKey}/update/averageCcfDuration`,
        newCcfDurationAverage,
        { authToken: authToken }
      );
      Put(
        `sapi/jira/${issueKey}/update/sourceCounts`,
        { ...newExpectedSourceCounts },
        { authToken: authToken }
      );
      Put(
        `sapi/jira/${issueKey}/update/sourceAttributeCounts`,
        { ...newExpectedAttributeCounts },
        { authToken: authToken }
      );
    },
    addComment: (issueKey, comment, authToken) =>
      Post(
        `sapi/jira/${issueKey}/comment?comment=${comment}`,
        undefined,
        { authToken: authToken }
      ),
    ticketStatuses: (authToken, ...tickets) =>
      Post(
        'sapi/jira/issues/status',
        { ids: tickets.join(',') },
        { authToken: authToken }
      ),
    updateDescription: (issue, comment, authToken) =>
      Post(
        `sapi/jira/${issue}/description`,
        comment,
        { authToken: authToken }
      )
  },
  jobBuilds: {
    fetchRecent: (days) =>
      Get(`sapi/jobBuilds/recent?days=${days}`),
    fetchByBuildIds: (...buildIds) =>
      Post('sapi/jobBuilds/byBuildIds', { ids: buildIds.join(',') }),
    fetchByJobIds: (...jobIds) =>
      Post('sapi/jobBuilds/byJobIds', { ids: jobIds.join(',') }),
    fetchLatestImageBaselines: () => Get('sapi/latest-image-baselines')
  },
  jobLabels: {
    fetchByJobIds: (...jobIds) => Post('sapi/jobLabelsByIds', { ids: jobIds.join(',') }),
    addByJobId: (jobId, label) => Post(`sapi/labels/job/${jobId}/add?label=${label}`),
    removeByJobId: (jobId, label) =>
      Put(`sapi/labels/job/${jobId}/remove?label=${label}`),
  },
  jobRelationships: {
    fetchJobRelationshipsByJobIds: (...jobIds) =>
      Post('sapi/jobrelationship', { ids: jobIds.join(',') }),
    fetchJobScenariosByScenarioIds: (...scenarioIds) =>
      Post('sapi/jobscenarios', { ids: scenarioIds.join(',') })
  },
  metrics: {
    fetchByJobIds: (requestHeader, ...jobIds) =>
      Post('sapi/metrics', { ids: jobIds.join(',') }, requestHeader)
  },
  nodesIssues: {
    fetchActiveNodeIssues: () =>
      Get('sapi/node/issues/active'),
    fetchNodeIssueTypes: (...ids) =>
      Post('sapi/node/issues/types', { ids: ids.join(',') }),
    markSingleInactive: (id) =>
      Post(`sapi/node/issues/active/single/deactivate/${id}`),
    markMultipleInactive: (...ids) =>
      Post('sapi/node/issues/active/collection/deactivate', { ids: ids.join(',') })
  },
  notifications: {
    fetchNotificationRequests: (userEmail) =>
      Get(`sapi/notifications/subscribed?user=${userEmail}`),
    fetchNotificationRules: () => Get('sapi/notifications/rules'),
    subscribe: (notificationRequest) =>
      Post('sapi/notifications/subscribe', { ...notificationRequest }),
    unsubscribe: (notificationRequest) =>
      Post('sapi/notifications/unsubscribe', { ...notificationRequest }),
  },
  params: {
    fetchParamsByJobIds: (requestHeader, ...jobIds) =>
      Post('sapi/params', { ids: jobIds.join(',') }, requestHeader),
    fetchParamsByNames: (paramName, jobName, afterTime) =>
      Get(`sapi/params/${paramName}/values?jobName=${jobName}&afterTime=${afterTime}`)
  },
  scenarios: {
    fetchByDispatcherIds: (...dispatcherIds) =>
      Post('sapi/scenariosSimple', { ids: dispatcherIds.join(',') })
  },
  sonar: {
    fetchSonarDataByBuilds: (baselineBuildId, currentBuildId) =>
      Get(`sapi/sonar/${baselineBuildId}?currentBuild=${currentBuildId}`)
  },
  telemetry: {
    addControlTelemetry: (requestHeader, controlName) =>
      Post('sapi/telemetry/control', { name: controlName }, requestHeader)
  },
  userConfiguration: {
    fetchUserConfiguration: (username) =>
      Get(`sapi/userConfiguration/get/${username}`),
    updateUserConfiguration: (userConfiguration) =>
      Post('sapi/userConfiguration/update', {
        'username': userConfiguration.username,
        'is_toast_muted': userConfiguration.muteToasts,
        'is_dark_mode_enabled': userConfiguration.darkMode,
        'custom_nav': JSON.stringify(userConfiguration.customNav)
      } )
  },
  webSocket: { fetchPort: () => Get('sapi/config/ws/port') },
  white: {
    fetchFailedTestArtifactsByJobIds: (...jobIds) =>
      Post('sapi/white/failedTestArtifacts', { ids: jobIds.join(',') })
  }
};

/**
 * Helper for determining a subset of ids that have not already been requested, either
 * pending or not
 * @param {{ inProgress: boolean, args: any[], isDone: any[] }} cachedRequest
 *  - Previously requested arguments and if the request is currently in progress.
 * @param {any[]} currentIds - List of all ids currently being requested.
 */
export const multiIdRequestCacheCheck = (cachedRequest, currentIds) => {
  // avoid nested arrays which can cause a problem
  const flatCurrentIds = flatten(currentIds);
  if (currentIds.filter(v => Array.isArray(v)).length > 0) {
    console.error('A nested array passed to multiIdRequestCacheCheck was corrected\
      to avoid conflict but should still be addressed at the call.');
  }

  if (cachedRequest && cachedRequest.args) {
    const differenceArgs = difference(flatCurrentIds, cachedRequest.args);
    const result = {
      inProgress: differenceArgs.length === 0,
      args: differenceArgs,
      allArgs: cachedRequest.args.concat(differenceArgs)
    };
    return result;
  }

  return {
    inProgress: false,
    args: flatCurrentIds,
    isDone: undefined
  };
};

/**
 * Helper to transform a map of key to object where the object is simply { key, value } to
 * one object with a new property for each of those pairings.
 * @param {{ [key: string]: { key: string, value: any }}} byId - The pairings
 */
export const idMapPairToDictionary = byId => Object.keys(byId).reduce((result, k) => {
  const values = byId[k];
  const map = values.reduce((result2, v) => {
    result2[v.name] = v.value;
    return result2;
  }, {});

  result[k] = map || {};
  return result;
}, {});

export const idMapWhiteToDictionary = byId => Object.keys(byId).reduce((result, k) => {
  const values = byId[k];
  const map = values.reduce((result2, v) => {
    for (const key in v.failedTestArtifacts) {
      result2[key] = {
        consoleLog: v.failedTestArtifacts[key].consoleLog,
        crashLog: v.failedTestArtifacts[key].crashLog,
        image: v.failedTestArtifacts[key].image,
        videoLink: v.failedTestArtifacts[key].videoLink,
        axiomLog: v.failedTestArtifacts[key].axiomLog,
        eventLog: v.failedTestArtifacts[key].eventLog,
        htmlLog: v.failedTestArtifacts[key].htmlLog
      };
    }
    return result2;
  }, {});

  result[k] = map || {};
  return result;
}, {});


// WEBPACK FOOTER //
// src/actions/api.js